import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import api from '../../services/api.ts';
import { formatCurrency } from '../../utils/currency.ts';
import FormPage from '../../components/ui/FormPage.tsx';
import StatusChip from '../../components/ui/StatusChip.tsx';


const formatPreviewDate = (raw: unknown): string => {
  if (raw == null || raw === '') return '—';
  const s = String(raw).trim();
  const isoDay = s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;
  const d = isoDay
    ? new Date(isoDay + 'T00:00:00')
    : new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
};

const ExecutarLancamentosAuto: React.FC = () => {
  const navigate = useNavigate();
  const [competencia, setCompetencia] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}_${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [error, setError] = useState('');

  const { data: preview, isLoading, refetch } = useQuery(
    ['auto-preview', competencia],
    async () => {
      const res = await api.post('/financeiro/lancamentos-automaticos/preview', { competencia });
      return res.data as {
        candidatos: any[];
        toCreate: number;
        duplicates: number;
        emptyRules: boolean;
        competencia: string;
      };
    }
  );

  const execMutation = useMutation(
    async () => {
      const res = await api.post('/financeiro/lancamentos-automaticos/executar', { competencia });
      return res.data;
    },
    {
      onSuccess: () => navigate('/financeiro/lancamentos-automaticos?tab=historico'),
      onError: (err: any) =>
        setError(err?.response?.data?.error || 'Erro ao executar lançamentos automáticos'),
    }
  );

  const toCreate = preview?.toCreate || 0;

  return (
    <FormPage
      backTo="/financeiro/lancamentos-automaticos?tab=historico"
      backLabel="Voltar ao histórico"
      breadcrumb="Financeiro / Lançamentos automáticos / Executar"
      title="Executar lançamentos automáticos"
      subtitle="Prévia e confirmação antes de gerar os lançamentos"
      hint="A prévia lista o que será criado. Lançamentos já gerados para a mesma competência não são duplicados."
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/financeiro/lancamentos-automaticos?tab=historico')}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={execMutation.isLoading || toCreate === 0 || preview?.emptyRules}
            onClick={() => {
              setError('');
              execMutation.mutate();
            }}
          >
            {execMutation.isLoading
              ? 'Gerando…'
              : `Confirmar ${toCreate} lançamento${toCreate === 1 ? '' : 's'}`}
          </button>
        </>
      }
    >
      {error && <div className="field-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="form-grid" style={{ marginBottom: 16 }}>
        <label className="field">
          <span>Competência (YYYY_MM)</span>
          <input
            className="input"
            value={competencia}
            onChange={e => setCompetencia(e.target.value)}
            onBlur={() => refetch()}
            placeholder="2026_09"
          />
        </label>
      </div>

      {preview?.emptyRules ? (
        <div className="hint-banner">
          Nenhuma regra ativa. <strong>Ative regras</strong> antes de executar.
        </div>
      ) : isLoading ? (
        <div className="muted">Carregando preview…</div>
      ) : (
        <>
          <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: 16 }}>
            <div className="kpi">
              <div className="kpi-label">A criar</div>
              <div className="kpi-value positive">{preview?.toCreate || 0}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Duplicatas evitadas</div>
              <div className="kpi-value">{preview?.duplicates || 0}</div>
            </div>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Origem</th>
                  <th>Descrição</th>
                  <th>Data</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Valor</th>
                </tr>
              </thead>
              <tbody>
                {(preview?.candidatos || []).map((c: any, idx: number) => (
                  <tr key={c.idempotencyKey || idx}>
                    <td>
                      <span className="chip chip-slate">
                        origem:{c.origemTipo}:{c.origemId}
                      </span>
                    </td>
                    <td>{c.description}</td>
                    <td className="mono">
                      {formatPreviewDate(c.movementDate)}
                    </td>
                    <td>
                      {c.duplicate ? (
                        <StatusChip label="Duplicata evitada" tone="amber" />
                      ) : (
                        <StatusChip label="Novo" tone="green" />
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }} className="amount">
                      {c.type === 'in' ? '+ ' : '− '}
                      {formatCurrency(c.amount)}
                    </td>
                  </tr>
                ))}
                {(preview?.candidatos || []).length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                      Nenhum candidato para esta competência.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </FormPage>
  );
};

export default ExecutarLancamentosAuto;
